import { useEffect, useState } from 'react'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { AdvisorEngineInboundContactSyncSettingsFilters, AdvisorEngineSettingsDefaultValues, AdvisorEngineSettingsFormValues } from './models'
import { Controller, useForm } from 'react-hook-form'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../components/Button/Button'

const ADVISOR_ENGINE_CONTACT_SYNC_MAX_OBJECT_COUNT = 500

export const AdvisorEngineContactSyncSettings = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })

  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'advisorengine',
    direction: 'inbound',
    object: 'contact_operation_hashed',
    scheduling: '*/30 * * * *',
    payload: {
      maxObjectCount: ADVISOR_ENGINE_CONTACT_SYNC_MAX_OBJECT_COUNT,
    },
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const form = useForm<AdvisorEngineSettingsFormValues>({ defaultValues: AdvisorEngineSettingsDefaultValues })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [serviceConfig, setServiceConfig] = useState<{ [key: string]: any } | null>(null)
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'advisorengine')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  useEffect(() => {
    if (userData) {
      const integration = userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.ADVISOR_ENGINE)
      if (integration) {
        setServiceConfig(integration.serviceConfig ?? null)
      }
    }
  }, [userData])

  useEffect(() => {
    if (serviceConfig?.inbound?.contact?.filters) {
      form.setValue('inbound.contact.filters', serviceConfig.inbound.contact.filters)
    }
  }, [serviceConfig, form])

  const onSubmit = async (data: AdvisorEngineSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ADVISOR_ENGINE)
      if (integration) {
        const filters: AdvisorEngineInboundContactSyncSettingsFilters = {
          active: data.inbound.contact.filters.active ?? undefined,
          contactType: data.inbound.contact.filters.contactType || undefined,
          tags: (data.inbound.contact.filters.tags ?? []).length > 0 ? data.inbound.contact.filters.tags : undefined,
        }

        const serviceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters } } })

        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(serviceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  if (!isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching)) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and Advisor Engine.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>

      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button
              variant='secondary'
              onClick={handleCancel}
              disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
            >
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button
                variant='primary'
                onClick={form.handleSubmit(onSubmit)}
                loading={isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading}
              >
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
